import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, firstValueFrom, Observable, of } from 'rxjs';
import { ContentLanguage, EquityElementTypeEnum } from '@coin/shared/core/util-enums';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { AuthService } from '@coin/shared/auth/data-access';
import { DynamicPageDataService } from '../../shared/models/dynamic-page-data-service.model';

interface Item {
  fileName: string;
}

@Injectable({
  providedIn: 'root'
})
export class EquityPageDataService implements DynamicPageDataService {
  private readonly httpClient = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly translateService = inject(TranslateService);

  @Output() newPage$ = new EventEmitter<Item>();

  private chapters: unknown[] = [];
  private isRow = true;
  private currentItem: Item;
  private isAuthenticatedState = false;

  public get canSwitchCountry(): boolean {
    return (
      this.currentItem?.fileName === 'direct-match-program/direct-match-program' ||
      this.currentItem?.fileName === 'employee-spin-off-incentive-program/employee-spin-off-incentive-program' ||
      this.currentItem?.fileName === 'download-center/download-center'
    );
  }

  constructor() {
    this.setIsRow();
    this.checkLoginState();
  }

  public getRowState(): boolean {
    return this.isRow;
  }

  private async setIsRow(): Promise<void> {
    const user = await this.authService.getUser();
    this.isRow = user?.['custom:country'] === undefined || user['custom:country'] !== 'DE';
  }

  private checkLoginState(): void {
    setTimeout(async () => {
      const isAuthenticated = this.authService.isAuthenticated$.value;
      const user = await this.authService.getUser();

      if (this.isAuthenticatedState !== isAuthenticated) {
        this.isAuthenticatedState = isAuthenticated;
        this.isRow = user?.['custom:country'] === undefined || user['custom:country'] !== 'DE';
        this.displayPage(null);
      }
      this.checkLoginState();
    }, 3000);
  }

  private getAssetDataByFileName(name: string, lang: string): Observable<unknown> {
    return name ? this.httpClient.get(`./assets/equity/json/${name}_${lang}.json`) : of(EMPTY);
  }

  public toggleROW(): void {
    this.isRow = !this.isRow;
    this.displayPage(null);
  }

  public async displayPage(item?: Item): Promise<void> {
    if (item) {
      this.currentItem = item;
    }

    const currLang: string =
      this.translateService?.currentLang === ContentLanguage.GERMAN ? ContentLanguage.GERMAN : ContentLanguage.ENGLISH;
    let data;
    if (this.currentItem?.fileName) {
      data = await firstValueFrom(this.getAssetDataByFileName(this.currentItem.fileName, currLang));
    }
    const newChapters = !this.isRow && this.canSwitchCountry ? data?.dtld?.chapters : data?.row?.chapters;

    if (newChapters && !TinyHelpers.areItemsSimilar(newChapters, this.chapters)) {
      this.chapters = newChapters.filter(async chapter =>
        chapter?.elements?.[0]?.type === EquityElementTypeEnum.DOWNLOAD ? this.authService.isAuthenticated$.value : true
      );
      this.newPage$.emit(this.currentItem);
    }
  }
}
